import _ from "lodash";

/**
 * Initializes the adjust data point tool in WPD.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	// cannot pick and choose functions to override due to how it was written
	// completely overwrite the manual selection tool into a js class
	wpd.AdjustDataPointTool = class {
		constructor(axis, dataset, positionOnly) {
			this.axis = axis;
			this.dataset = dataset;
			this.positionOnly = positionOnly;

			// multi-select box
			this.isMouseDown = false;
			this.isSelecting = false;
			this._drawTimer = null;
			this.p1 = null;
			this.p2 = null;
			this.imageP1 = null;
			this.imageP2 = null;

			this.resolvingOverlappingPixels = false;
			this._hoverDebounceTimer = null;

			this.$button = this.positionOnly
				? wpdDocument.getElementById("move-point-button")
				: wpdDocument.getElementById("manual-adjust-button");
		}

		static #cursorDistance = 25;

		onAttach() {
			this.$button.classList.add("pressed-button");
			wpd.graphicsWidget.setRepainter(
				new wpd.DataPointsRepainter(this.axis, this.dataset),
			);

			if (!dataEntryVM.undoRedoEnabled) {
				wpd.toolbar.show("adjustDataPointsToolbar");
			}

			if (wpd.tagGroups.activeTagGroup) {
				// display tag group ui
				// active tag group is only set when in text extraction mode
				wpd.tagGroups.showControls();
			}

			if (wpd.custom.isGraphAxis(this.axis)) {
				const pixelIndexes = this.dataset.getSelectedPixels();

				if (pixelIndexes.length > 0) {
					// immediately engage the metadata dialog if this is a graph
					// and there are data point(s) selected
					wpd.events.dispatch("wpd.dataset.point.select", {
						axes: this.axis,
						dataset: this.dataset,
						indexes: pixelIndexes,
					});
				}
			}
		}

		onRemove() {
			// clear selected data points
			//TODO: Removing this as it doesn't seem necessary and caused visual bugs;
			// likely suspect if something breaks
			// this.dataset.unselectAll();

			wpd.graphicsWidget.forceHandlerRepaint();

			this.$button.classList.remove("pressed-button");
			wpd.toolbar.clear();
		}

		onMouseDown(ev, pos, imagePos) {
			this.isMouseDown = true;

			// record the first selection rectangle point
			this.p1 = pos;
			this.imageP1 = imagePos;

			// unselect everything
			this.dataset.unselectAll();
		}

		onMouseUp(ev, pos) {
			if (this.isSelecting === true) {
				// reset hover context to remove selection box drawing
				wpd.graphicsWidget.resetHover();

				// select points within the selection rectangle
				this.dataset.selectPixelsInRectangle(this.imageP1, this.imageP2);

				this.#onSelect(ev, this.dataset.getSelectedPixels());

				// clear the draw timer
				clearTimeout(this._drawTimer);

				// push these reset statements to the bottom of the events message queue
				setTimeout(
					function () {
						this.isSelecting = false;
						this.isMouseDown = false;
						this.p1 = null;
						this.p2 = null;

						// reset hover context to remove previous selection box
						wpd.graphicsWidget.resetHover();
					}.bind(this),
				);
			} else {
				this.isMouseDown = false;
				this.p1 = null;
				this.p2 = null;

				// reset hover context to remove previous selection box
				wpd.graphicsWidget.resetHover();
			}
		}

		onMouseMove(ev, pos, imagePos) {
			if (this.isMouseDown === true) {
				this.p2 = pos;

				const area = (this.p2.x - this.p1.x) * (this.p2.y - this.p1.y);
				if (area >= 300) {
					this.isSelecting = true;
				}

				this.imageP2 = imagePos;

				// refresh the selection rectangle every 1 ms
				clearTimeout(this._drawTimer);
				this._drawTimer = setTimeout(
					function () {
						this.drawSelectionBox();
					}.bind(this),
					1,
				);
			} else {
				const pxIndex = this.dataset.findNearestPixel(
					imagePos.x,
					imagePos.y,
					wpd.AdjustDataPointTool.#cursorDistance,
				);

				if (dataEntryVM.viewEditOverlappingDataPoints) {
					if (this.resolvingOverlappingPixels) {
						return;
					}

					// clear existing debounce timer
					if (this._hoverDebounceTimer) {
						clearTimeout(this._hoverDebounceTimer);
						this._hoverDebounceTimer = null;
					}

					if (pxIndex > -1) {
						const foundPoints = [];
						// only display overlapping pixels for text data
						if (wpd.custom.identifyAxis(this.axis) === "image") {
							foundPoints.push(...this.dataset.findOverlappingPixels(pxIndex));
						} else {
							foundPoints.push({
								pixel: this.dataset.getPixel(pxIndex),
								index: pxIndex,
							});
						}
						this.#onHoverV2(ev, foundPoints);
					} else {
						this.#resetHover();
					}
				} else {
					let foundPoint = null;
					if (pxIndex > -1) {
						foundPoint = {
							pixel: this.dataset.getPixel(pxIndex),
							index: pxIndex,
						};
					}

					this.#resetHover();

					if (foundPoint) {
						this.#onHover(ev, foundPoint);
					}
				}
			}
		}

		#resetHover() {
			const ctx = wpd.graphicsWidget.getAllContexts();
			let canvasDims = wpd.graphicsWidget.screenPx(
				wpd.graphicsWidget.originalWidth,
				wpd.graphicsWidget.originalHeight,
			);
			ctx.hoverCtx.clearRect(0, 0, canvasDims.x, canvasDims.y);
			wpd.events.dispatch("wpd.dataset.point.closeHover");
		}

		drawSelectionBox() {
			// reset hover context to remove previous selection box
			wpd.graphicsWidget.resetHover();

			// fetch the hover context
			const ctx = wpd.graphicsWidget.getAllContexts().hoverCtx;

			// draw a black rectangle
			if (this.p1 != null && this.p2 != null) {
				ctx.strokeStyle = wpd.Colors.default;
				ctx.strokeRect(
					this.p1.x,
					this.p1.y,
					this.p2.x - this.p1.x,
					this.p2.y - this.p1.y,
				);
			}
		}

		#onHover(ev, point) {
			var pixel = point.pixel;
			var index = point.index;
			var screenPx = wpd.graphicsWidget.screenPx(pixel.x, pixel.y),
				ctx = wpd.graphicsWidget.getAllContexts();

			ctx.hoverCtx.beginPath();
			ctx.hoverCtx.strokeStyle = "rgb(240, 157, 55)";
			ctx.hoverCtx.lineWidth = 2;
			ctx.hoverCtx.arc(screenPx.x, screenPx.y, 4, 0, 2.0 * Math.PI, true);
			ctx.hoverCtx.stroke();

			wpd.events.dispatch("wpd.dataset.point.hover", {
				axes: this.axis,
				dataset: this.dataset,
				index: index,
			});
		}

		#onHoverV2(ev, points) {
			if (points.length === 0) {
				return;
			}

			const pixel = points[0].pixel;
			this.#drawDataPointHighlight(pixel);

			// debounce hover with 200ms delay
			this._hoverDebounceTimer = setTimeout(() => {
				this.#resetHover();
				this.#drawDataPointHighlight(pixel);

				wpd.events.dispatch("wpd.dataset.point.hover", {
					axes: this.axis,
					dataset: this.dataset,
					indexes: points.map(({ index }) => index),
				});

				this._hoverDebounceTimer = null;
			}, 200);
		}

		#drawDataPointHighlight(pixel) {
			if (!pixel) {
				return;
			}

			const screenPx = wpd.graphicsWidget.screenPx(pixel.x, pixel.y);
			const ctx = wpd.graphicsWidget.getAllContexts();

			ctx.hoverCtx.beginPath();
			ctx.hoverCtx.strokeStyle = "rgb(240, 157, 55)";
			ctx.hoverCtx.lineWidth = 2;
			ctx.hoverCtx.arc(screenPx.x, screenPx.y, 4, 0, 2.0 * Math.PI, true);
			ctx.hoverCtx.stroke();
		}

		async #onSelect(ev, pixelIndexes) {
			wpd.graphicsWidget.forceHandlerRepaint();
			wpd.graphicsWidget.updateZoomOnEvent(ev);
			this.#resetHover();

			// clear existing debounce timer
			if (this._hoverDebounceTimer) {
				clearTimeout(this._hoverDebounceTimer);
				this._hoverDebounceTimer = null;
			}

			// only resolve overlapping pixels if user selected a single pixel
			// and we are not already resolving overlapping pixels
			if (
				dataEntryVM.viewEditOverlappingDataPoints &&
				pixelIndexes.length < 2 &&
				!this.resolvingOverlappingPixels
			) {
				// check for overlapping pixels for each pixel index
				let selectedIndexes = [];
				for (const index of pixelIndexes) {
					const overlappingPixels = this.dataset.findOverlappingPixels(index);

					// if there are overlapping pixels, open the metadata select dialog
					if (overlappingPixels.length > 1) {
						this.resolvingOverlappingPixels = true;

						const selectedIndex = await wpd.custom.openMetadataSelectDialog(
							overlappingPixels,
						);

						if (selectedIndex !== undefined) {
							selectedIndexes.push(selectedIndex);
						}

						this.resolvingOverlappingPixels = false;
					} else {
						selectedIndexes.push(index);
					}
				}

				wpd.events.dispatch("wpd.dataset.point.select", {
					axes: this.axis,
					dataset: this.dataset,
					indexes: selectedIndexes,
					positionOnly: this.positionOnly,
				});
			} else {
				wpd.events.dispatch("wpd.dataset.point.select", {
					axes: this.axis,
					dataset: this.dataset,
					indexes: pixelIndexes,
					positionOnly: this.positionOnly,
				});
			}
		}

		onMouseClick(ev, pos, imagePos) {
			if (this.isSelecting === false) {
				this.dataset.unselectAll();
				const pixelIndex = this.dataset.selectNearestPixel(
					imagePos.x,
					imagePos.y,
					wpd.AdjustDataPointTool.#cursorDistance,
				);
				this.#onSelect(ev, [pixelIndex]);
			}
		}

		onKeyDown(ev) {
			if (wpd.acquireData.isToolSwitchKey(ev.keyCode)) {
				wpd.acquireData.switchToolOnKeyPress(
					String.fromCharCode(ev.keyCode).toLowerCase(),
				);
				return;
			}

			const selIndexes = this.dataset.getSelectedPixels();
			if (selIndexes.length < 1) {
				return;
			}

			// key strokes that do not need each point processed
			if (wpd.keyCodes.isAlphabet(ev.keyCode, "r")) {
				wpd.dataPointValueOverrideEditor.show(
					this.dataset,
					this.axis,
					selIndexes,
					this,
				);
				return;
			}

			// key strokes that need each point processed
			let lastPtCoord = {
				x: null,
				y: null,
			};

			const axisType = wpd.custom.identifyAxis(this.axis);
			selIndexes.forEach(
				function (selIndex) {
					const stepSize =
						ev.shiftKey === true
							? 5 / wpd.graphicsWidget.getZoomRatio()
							: 0.5 / wpd.graphicsWidget.getZoomRatio();

					let selPoint = this.dataset.getPixel(selIndex);

					if (!this.positionOnly || !selPoint) return;
					let pointPx = selPoint.x;
					let pointPy = selPoint.y;

					// rotate to current rotation
					const currentRotation = wpd.graphicsWidget.getRotation();

					let x, y;
					const { x: origX, y: origY } =
						wpd.graphicsWidget.getRotatedCoordinates(
							0,
							currentRotation,
							pointPx,
							pointPy,
						);

					if (wpd.keyCodes.isUp(ev.keyCode)) {
						x = origX;
						y = origY - stepSize;
					} else if (wpd.keyCodes.isDown(ev.keyCode)) {
						x = origX;
						y = origY + stepSize;
					} else if (wpd.keyCodes.isLeft(ev.keyCode)) {
						x = origX - stepSize;
						y = origY;
					} else if (wpd.keyCodes.isRight(ev.keyCode)) {
						x = origX + stepSize;
						y = origY;
					} else {
						return;
					}

					// check if the selected data point belongs to a tuple
					// if so, update data points within the tuple
					const metadata = this.axis.getMetadata();
					if (dataEntryVM.pointGroupOverhaul) {
						if (this.dataset.hasPointGroups()) {
							let pointIndexes = [selIndex];

							const initialRotation = metadata.initialRotation;
							const rotationDifference = Math.abs(
								currentRotation - initialRotation,
							);
							const swapXY =
								rotationDifference > 0 && rotationDifference % 180 !== 0;

							// update each data point in the tuple if the change is in
							// the shared direction
							let updateAxis = "";
							if (x - origX !== 0) {
								if (
									metadata.pointGroups[swapXY ? "x" : "y"] &&
									selPoint.metadata.tuple?.[swapXY ? "x" : "y"]
								) {
									pointIndexes = wpd.pointGroups.getTupleDataPointIndexes(
										this.dataset,
										selPoint,
										swapXY ? "x" : "y",
									);
								}
								updateAxis = swapXY ? "y" : "x";
							} else if (y - origY !== 0) {
								if (
									metadata.pointGroups[swapXY ? "y" : "x"] &&
									selPoint.metadata.tuple?.[swapXY ? "y" : "x"]
								) {
									pointIndexes = wpd.pointGroups.getTupleDataPointIndexes(
										this.dataset,
										selPoint,
										swapXY ? "y" : "x",
									);
								}
								updateAxis = swapXY ? "x" : "y";
							}

							if (dataEntryVM.pointGroupColors) {
								const selectedAxis = selPoint.metadata.pointGroupAxisDirection;
								for (const index of pointIndexes) {
									const dataPoint = this.dataset.getPixel(index);

									const { x: dpX, y: dpY } =
										wpd.graphicsWidget.getRotatedCoordinates(
											0,
											currentRotation,
											dataPoint.x,
											dataPoint.y,
										);

									const dataPointAxis =
										dataPoint.metadata.pointGroupAxisDirection;
									const updateCoordinates = [];

									if (selectedAxis === "center") {
										if (
											updateAxis === "x" &&
											["center", "y"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(dpX, y);
											} else {
												updateCoordinates.push(x, dpY);
											}
										} else if (
											updateAxis === "y" &&
											["center", "x"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(x, dpY);
											} else {
												updateCoordinates.push(dpX, y);
											}
										} else {
											continue;
										}
									} else if (selectedAxis === "x") {
										if (
											updateAxis === "y" &&
											["center", "x"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(x, dpY);
											} else {
												updateCoordinates.push(dpX, y);
											}
										} else if (
											updateAxis === "x" &&
											["center", "x"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(dpX, y);
											} else {
												updateCoordinates.push(x, dpY);
											}
										} else {
											continue;
										}
									} else if (selectedAxis === "y") {
										if (
											updateAxis === "x" &&
											["center", "y"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(dpX, y);
											} else {
												updateCoordinates.push(x, dpY);
											}
										} else if (
											updateAxis === "y" &&
											["center", "y"].includes(dataPointAxis)
										) {
											if (swapXY) {
												updateCoordinates.push(x, dpY);
											} else {
												updateCoordinates.push(dpX, y);
											}
										} else {
											continue;
										}
									} else {
										continue;
									}

									const { x: rotatedX, y: rotatedY } =
										wpd.graphicsWidget.getRotatedCoordinates(
											currentRotation,
											0,
											updateCoordinates[0],
											updateCoordinates[1],
										);

									this.dataset.setPixelAt(index, rotatedX, rotatedY);

									// set zoom to the selected data point
									if (index === selIndex) {
										lastPtCoord = {
											x: x,
											y: y,
										};
									}
								}
							} else {
								// rotate back to original rotation
								({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
									currentRotation,
									0,
									x,
									y,
								));
								for (const index of pointIndexes) {
									const dataPoint = this.dataset.getPixel(index);
									const updateCoordinates = [];
									if (updateAxis === "x") {
										updateCoordinates.push(x, dataPoint.y);
									} else {
										updateCoordinates.push(dataPoint.x, y);
									}

									this.dataset.setPixelAt(index, ...updateCoordinates);

									// set zoom to the selected data point
									if (index === selIndex) {
										lastPtCoord = {
											x: x,
											y: y,
										};
									}
								}
							}

							// update point values if adjusting graph points

							if (axisType === "xy") {
								const skipUpdateAxis = updateAxis === "x" ? "y" : "x";

								wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									pointIndexes,
									skipUpdateAxis,
								);
							} else if (axisType === "bar") {
								const barOrientation = metadata.barHeaders.barOrientation;
								const swapAxes = currentRotation && currentRotation % 180 !== 0;
								let skipUpdateAxis =
									barOrientation === "VERTICAL"
										? swapAxes
											? "y"
											: "x"
										: swapAxes
										? "x"
										: "y";

								if (swapXY) {
									skipUpdateAxis = skipUpdateAxis === "x" ? "y" : "x";
								}
								wpd.barGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									pointIndexes,
									skipUpdateAxis,
								);
							}

							// update tuple ids to center point after value updates
							wpd.pointGroups.syncTupleIdsToCenterPoint(this.dataset, selPoint);
						} else {
							// rotate back to original rotation
							({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
								currentRotation,
								0,
								x,
								y,
							));
							this.dataset.setPixelAt(selIndex, x, y);
							lastPtCoord = {
								x: x,
								y: y,
							};

							// update point values if adjusting graph points
							if (axisType === "xy") {
								wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									[selIndex],
								);
							} else if (axisType === "bar") {
								wpd.barGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									[selIndex],
								);
							}
						}
					} else {
						if (wpd.pointGroups.hasPointGroups(metadata)) {
							// get the tuple
							const tupleIndex = this.dataset.getTupleIndex(selIndex);
							let pointIndexes = [selIndex];

							// update each data point in the tuple if the change is in
							// the shared direction
							let updateAxis = "";
							if (x - origX !== 0 && metadata.pointGroups.y) {
								pointIndexes = this.dataset.getTuple(tupleIndex);
								updateAxis = "x";
							} else if (y - origY !== 0 && metadata.pointGroups.x) {
								pointIndexes = this.dataset.getTuple(tupleIndex);
								updateAxis = "y";
							}

							pointIndexes = pointIndexes.filter((i) => i !== null);

							if (dataEntryVM.pointGroupColors) {
								const selectedAxis = selPoint.metadata.tagGroupAxisDirection;
								({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
									currentRotation,
									0,
									x,
									y,
								));
								for (const index of pointIndexes) {
									const dataPoint = this.dataset.getPixel(index);
									const dataPointAxis =
										dataPoint.metadata.tagGroupAxisDirection;
									const updateCoordinates = [];

									if (selectedAxis === "center") {
										if (
											updateAxis === "x" &&
											["center", "y"].includes(dataPointAxis)
										) {
											updateCoordinates.push(x, dataPoint.y);
										} else if (
											updateAxis === "y" &&
											["center", "x"].includes(dataPointAxis)
										) {
											updateCoordinates.push(dataPoint.x, y);
										} else {
											continue;
										}
									} else if (selectedAxis === "x") {
										if (
											updateAxis === "y" &&
											["center", "x"].includes(dataPointAxis)
										) {
											updateCoordinates.push(dataPoint.x, y);
										} else {
											continue;
										}
									} else if (selectedAxis === "y") {
										if (
											updateAxis === "x" &&
											["center", "y"].includes(dataPointAxis)
										) {
											updateCoordinates.push(x, dataPoint.y);
										} else {
											continue;
										}
									} else {
										continue;
									}

									this.dataset.setPixelAt(index, ...updateCoordinates);

									// set zoom to the selected data point
									if (index === selIndex) {
										lastPtCoord = {
											x: x,
											y: y,
										};
									}
								}

								if (updateAxis === selPoint.metadata.tagGroupAxisDirection) {
									this.dataset.setPixelAt(selIndex, x, y);
									lastPtCoord = {
										x: x,
										y: y,
									};
								}
							} else {
								// rotate back to original rotation
								({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
									currentRotation,
									0,
									x,
									y,
								));
								for (const index of pointIndexes) {
									const dataPoint = this.dataset.getPixel(index);
									const updateCoordinates = [];
									if (updateAxis === "x") {
										updateCoordinates.push(x, dataPoint.y);
									} else {
										updateCoordinates.push(dataPoint.x, y);
									}

									this.dataset.setPixelAt(index, ...updateCoordinates);

									// set zoom to the selected data point
									if (index === selIndex) {
										lastPtCoord = {
											x: x,
											y: y,
										};
									}
								}
							}

							// update point values if adjusting graph points
							if (axisType === "xy") {
								wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									pointIndexes,
								);
							} else if (axisType === "bar") {
								wpd.barGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									pointIndexes,
								);
							}
						} else {
							// rotate back to original rotation
							({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
								currentRotation,
								0,
								x,
								y,
							));
							this.dataset.setPixelAt(selIndex, x, y);
							lastPtCoord = {
								x: x,
								y: y,
							};

							// update point values if adjusting graph points
							if (axisType === "xy") {
								wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									[selIndex],
								);
							} else if (axisType === "bar") {
								wpd.barGraphEdit.updateGraphPointsWithAxisPointValues(
									this.axis,
									this.dataset,
									[selIndex],
								);
							}
						}
					}
				}.bind(this),
			);

			wpd.graphicsWidget.forceHandlerRepaint();
			if (lastPtCoord.x != null) {
				wpd.graphicsWidget.updateZoomToImagePosn(lastPtCoord.x, lastPtCoord.y);
			}
			ev.preventDefault();
			ev.stopPropagation();
		}
	};
}

export { init };
